import React, { useState, useRef, useEffect } from 'react';
import { Send, MessageCircle, X, User as UserIcon, Paperclip, FileText, Image as ImageIcon, Download, ExternalLink, Loader2, Eye } from 'lucide-react';
import { UserAvatar } from '../UI/UserAvatar';
import { FilePreviewModal, FilePreviewItem } from '../UI/FilePreviewModal';
import { apiService } from '../../services/api';
import type { ChatMessage, User as UserType, Issue } from '../../types';

interface IssueMessengerProps {
  issue: Issue;
  messages: ChatMessage[];
  users: UserType[];
  currentUser: UserType;
  onSendMessage: (content: string, file?: File) => Promise<void> | void;
  isOpen: boolean;
  onClose: () => void;
}

export const IssueMessenger: React.FC<IssueMessengerProps> = ({
  issue,
  messages,
  users,
  currentUser,
  onSendMessage,
  isOpen,
  onClose,
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [previewModalFile, setPreviewModalFile] = useState<FilePreviewItem | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (selectedFile) {
      const isImg = selectedFile.type.startsWith('image/');
      if (isImg) {
        const url = URL.createObjectURL(selectedFile);
        setFilePreviewUrl(url);
        return () => URL.revokeObjectURL(url);
      } else {
        setFilePreviewUrl(null);
      }
    } else {
      setFilePreviewUrl(null);
    }
  }, [selectedFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        alert('File size must be under 50MB');
        return;
      }
      setSelectedFile(file);
    }
    // reset input so same file can be re-selected if removed
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreviewUrl(null);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedFile) || isSending) return;

    try {
      setIsSending(true);
      await onSendMessage(newMessage.trim(), selectedFile || undefined);
      setNewMessage('');
      setSelectedFile(null);
      setFilePreviewUrl(null);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const isImageFile = (fileName?: string, fileUrl?: string, msgType?: string) => {
    if (msgType === 'image') return true;
    const name = (fileName || fileUrl || '').toLowerCase();
    return /\.(png|jpg|jpeg|gif|webp|svg|bmp)$/i.test(name);
  };

  const isPdfFile = (fileName?: string, fileUrl?: string) => {
    const name = (fileName || fileUrl || '').toLowerCase();
    return /\.pdf$/i.test(name);
  };

  const handleOpenPreview = (fileUrl: string, fileName?: string) => {
    const isImg = isImageFile(fileName, fileUrl);
    const isPdf = isPdfFile(fileName, fileUrl);
    const type = isImg ? 'image' : isPdf ? 'pdf' : 'doc';
    const viewUrl = apiService.getFileViewUrl(fileUrl, fileName);

    setPreviewModalFile({
      url: viewUrl,
      name: fileName || (isImg ? 'Image' : 'Attachment'),
      type
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-in fade-in duration-150">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl h-[650px] flex flex-col overflow-hidden border border-gray-100">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50/80 via-indigo-50/40 to-white">
            <div className="flex items-center space-x-3 truncate mr-4">
              <div className="p-2 bg-blue-600 text-white rounded-xl shadow-sm">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div className="truncate">
                <h3 className="font-semibold text-gray-900 text-base flex items-center space-x-2">
                  <span>Chat - {issue.key}</span>
                  <span className="text-xs bg-blue-100 text-blue-800 font-medium px-2 py-0.5 rounded-full">
                    {messages.length}
                  </span>
                </h3>
                <p className="text-xs text-gray-500 truncate mt-0.5">{issue.summary}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
              title="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/60">
            {messages.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-inner">
                  <MessageCircle className="w-8 h-8 opacity-75" />
                </div>
                <p className="text-gray-700 font-medium mb-1">No messages yet for this ticket.</p>
                <p className="text-xs text-gray-400">Start discussing this issue with your team!</p>
              </div>
            ) : (
              messages.map(message => {
                const isCurrentUser = message.userId === currentUser.id;
                const rawAuthor = isCurrentUser ? currentUser : message.user || users.find(u => u.id === message.userId);
                const hasFile = Boolean(message.fileUrl);
                const isImg = hasFile && isImageFile(message.fileName, message.fileUrl, message.type);

                // Check if message came from Slack
                const isSlackMsg = message.content?.startsWith('[Slack');
                let displayAuthor = rawAuthor?.displayName;
                let displayContent = message.content;

                if (isSlackMsg && message.content.startsWith('[Slack - ')) {
                  const endBracket = message.content.indexOf(']');
                  if (endBracket !== -1) {
                    displayAuthor = message.content.substring(9, endBracket).trim();
                    displayContent = message.content.substring(endBracket + 1).trim();
                  }
                } else if (isSlackMsg && message.content.startsWith('[Slack] ')) {
                  const colonIdx = message.content.indexOf(':');
                  if (colonIdx !== -1) {
                    displayAuthor = message.content.substring(8, colonIdx).trim();
                    displayContent = message.content.substring(colonIdx + 1).trim();
                  }
                }

                return (
                  <div
                    key={message.id}
                    className={`flex items-start space-x-2.5 ${isCurrentUser ? 'justify-end flex-row-reverse space-x-reverse' : ''}`}
                  >
                    <UserAvatar
                      name={displayAuthor || rawAuthor?.displayName}
                      src={rawAuthor?.avatarUrl}
                      className="w-8 h-8 shadow-xs flex-shrink-0 mt-0.5"
                    />
                    <div className={`max-w-[78%] flex flex-col ${isCurrentUser ? 'items-end' : 'items-start'}`}>
                      <div className="flex items-center space-x-1.5 mb-1 px-1">
                        <span className="text-[11px] font-semibold text-gray-600">
                          {displayAuthor || 'User'}
                        </span>
                        {isSlackMsg && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-[#4A154B]/10 text-[#4A154B] border border-[#4A154B]/20">
                            Slack
                          </span>
                        )}
                      </div>
                      
                      <div
                        className={`rounded-2xl px-4 py-2.5 shadow-xs text-sm transition-all ${
                          isCurrentUser
                            ? 'bg-blue-600 text-white rounded-tr-xs'
                            : 'bg-white text-gray-900 border border-gray-200/80 rounded-tl-xs'
                        }`}
                      >
                        {/* Text message content */}
                        {displayContent && (
                          <p className="whitespace-pre-wrap break-words leading-relaxed">{displayContent}</p>
                        )}

                        {/* File Attachment Presentation */}
                        {hasFile && message.fileUrl && (
                          <div className={`mt-2 ${message.content ? 'pt-2 border-t border-black/10' : ''}`}>
                            {isImg ? (
                              <div
                                onClick={() => handleOpenPreview(message.fileUrl!, message.fileName)}
                                className="group/img relative rounded-xl overflow-hidden cursor-pointer border border-black/10 shadow-sm max-w-[280px] bg-black/5 hover:opacity-95 transition-all"
                              >
                                <img
                                  src={apiService.getFileViewUrl(message.fileUrl, message.fileName)}
                                  alt={message.fileName || 'Attachment'}
                                  className="w-full max-h-48 object-cover rounded-xl"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center space-x-2 text-white">
                                  <Eye className="w-5 h-5 drop-shadow" />
                                  <span className="text-xs font-medium drop-shadow">Click to Preview</span>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={() => handleOpenPreview(message.fileUrl!, message.fileName)}
                                className={`flex items-center space-x-3 p-2.5 rounded-xl cursor-pointer border transition-all ${
                                  isCurrentUser
                                    ? 'bg-white/10 hover:bg-white/20 border-white/20 text-white'
                                    : 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900'
                                }`}
                              >
                                <div className={`p-2 rounded-lg ${isCurrentUser ? 'bg-white/20 text-white' : 'bg-blue-50 text-blue-600'}`}>
                                  {isPdfFile(message.fileName, message.fileUrl) ? (
                                    <FileText className="w-5 h-5 text-red-400" />
                                  ) : (
                                    <FileText className="w-5 h-5" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0 pr-2">
                                  <p className="text-xs font-medium truncate" title={message.fileName}>
                                    {message.fileName || 'Attached Document'}
                                  </p>
                                  <p className={`text-[10px] ${isCurrentUser ? 'text-blue-100' : 'text-gray-500'}`}>
                                    Click to preview
                                  </p>
                                </div>
                                <a
                                  href={apiService.getFileViewUrl(message.fileUrl, message.fileName, true)}
                                  download={message.fileName}
                                  onClick={e => e.stopPropagation()}
                                  className={`p-1.5 rounded-lg transition-colors ${
                                    isCurrentUser ? 'hover:bg-white/20 text-white' : 'hover:bg-gray-200 text-gray-500'
                                  }`}
                                  title="Download"
                                >
                                  <Download className="w-4 h-4" />
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <span className="text-[10px] text-gray-400 mt-1 px-1">
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Pending File Attachment Preview */}
          {selectedFile && (
            <div className="px-5 py-2.5 bg-blue-50/70 border-t border-blue-100 flex items-center justify-between animate-in slide-in-from-bottom-2 duration-150">
              <div className="flex items-center space-x-3 overflow-hidden mr-3">
                {filePreviewUrl ? (
                  <img
                    src={filePreviewUrl}
                    alt="Preview"
                    className="w-10 h-10 object-cover rounded-lg border border-blue-200 shadow-2xs"
                  />
                ) : (
                  <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5" />
                  </div>
                )}
                <div className="truncate">
                  <p className="text-xs font-semibold text-blue-900 truncate">{selectedFile.name}</p>
                  <p className="text-[11px] text-blue-600">
                    {(selectedFile.size / 1024).toFixed(1)} KB • Ready to send
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-lg transition-colors"
                title="Remove attachment"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Input Bar */}
          <form onSubmit={handleSendMessage} className="p-3.5 border-t border-gray-200 bg-white">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
              className="hidden"
            />
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`p-2.5 rounded-xl transition-colors ${
                  selectedFile
                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title="Attach image or document"
              >
                <Paperclip className="w-5 h-5" />
              </button>

              <input
                type="text"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                placeholder={selectedFile ? "Add a message or press Enter to send..." : "Type your message..."}
                disabled={isSending}
                className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-sm transition-all"
              />

              <button
                type="submit"
                disabled={(!newMessage.trim() && !selectedFile) || isSending}
                className="p-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-xl shadow-xs transition-colors flex items-center justify-center"
                title="Send message"
              >
                {isSending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={Boolean(previewModalFile)}
        onClose={() => setPreviewModalFile(null)}
        file={previewModalFile}
      />
    </>
  );
};
